import { ISlideFeature, SlideInterface } from '@tencent/docs-api';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import {
    IThemeColorManager,
    EShowModalSource,
} from '@src/slide/application/service/theme-color-manager/index.interface';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { IPcEditorEditableServiceLayer } from '@src/slide/application/entry/toc/editor/pc/service-layer/editable-service-layer.interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IQuickToolDialogManager } from '@src/slide/application/service/quick-tool-dialog-manager/core/quick-tool-dialog-manager.interface';
import { QUICK_TOOL_AID_POSITION } from '@src/slide/application/service/quick-tool-dialog-manager/core';
import type { ICustomOptionsProps } from '@src/slide/application/service/export-image-manager';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IMasterLayoutEditService } from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import { EDIT_TYPE } from '@src/slide/domain/selection/constant/edit-type';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';

type IResult = SlideInterface.IResult;

export default class SlideFeature implements ISlideFeature {
    private static instance: ISlideFeature;
    public static getInstance(): ISlideFeature {
        if (!this.instance) {
            this.instance = new SlideFeature();
        }

        return this.instance;
    }

    /**
     * 展示更多主题弹窗
     * @returns IResult
     */
    public showThemeModal(props?: SlideInterface.IShowThemeModalProps): IResult {
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.SWITCH_COLOR,
            action: TDWActionType.AI_MORE,
        });
        try {
            const params = props && { source: EShowModalSource.ai, ...props };
            this.getThemeColorManager()?.showThemeModal(params);
            return { res: 'success' };
        } catch {
            return { res: 'fail' };
        }
    }

    /**
     * 字体弹窗（更多设置）
     * @param props
     */
    public async showFontModal(props: SlideInterface.IShowFontModalProps = {}): Promise<IResult> {
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.SWITCH_FONT,
            action: TDWActionType.AI_MORE,
        });
        try {
            const { onSelect, onCancel } = props;
            const quickToolDialogManager = await this.getQuickToolDialogManager()?.promise();
            await quickToolDialogManager?.showDialog(
                ActionType.UNIFORM_FONT,
                QUICK_TOOL_AID_POSITION.AI,
                () => onSelect?.(),
                () => onCancel?.(),
            );
            return { res: 'success' };
        } catch {
            return { res: 'fail' };
        }
    }

    /**
     * 导出为本地文件
     * @returns Promise<IResult>
     */
    public async exportNative(): Promise<IResult> {
        try {
            // eslint-disable-next-line no-underscore-dangle
            await this.getMenuManager()?._actionNativePPT();
            return { res: 'success' };
        } catch {
            return { res: 'fail' };
        }
    }

    /**
     * 导出为 PDF (唤起导出pdf的弹窗)
     * @returns IResult
     */
    public exportPDF(): IResult {
        try {
            this.getMenuManager()?.exportPDF();
            return { res: 'success' };
        } catch {
            return { res: 'fail' };
        }
    }

    /**
     * 导出为图片 (唤起导出图片的弹窗)
     * @param options ICustomOptionsProps
     * @returns IResult
     */
    public exportImage(options?: ICustomOptionsProps): IResult {
        try {
            this.getMenuManager()?.exportImage(options);
            return { res: 'success' };
        } catch {
            return { res: 'fail' };
        }
    }

    /**
     * 切换到元素对应的页面并选中指定元素
     * @param element slide 元素的引用
     */
    public positionElement(uiElement: SlideInterface.IElementUIModel): IResult {
        const selectionService = this.getSelectionServices();

        const getSlideIndex = (element: IElementBaseModel) => {
            const masterLayoutEditService = this.getMasterLayoutEditService();
            if (element.slideRef) {
                masterLayoutEditService.updateEditType(EDIT_TYPE.SLIDE);
                selectionService.currentSlideType = UIModelType.SLIDE;
                return element.slideRef.listIndex;
            }
            if (element.layoutRef) {
                masterLayoutEditService.updateEditType(EDIT_TYPE.MASTER_LAYOUT);
                selectionService.currentSlideType = UIModelType.LAYOUT;
                const { presentation } = this.getAppServices().getUiDataService();
                return presentation.layoutList.findIndex(i => i.id === element.layoutRef?.id);
            }
            if (element.masterRef) {
                masterLayoutEditService.updateEditType(EDIT_TYPE.MASTER_LAYOUT);
                selectionService.currentSlideType = UIModelType.MASTER;
                const { presentation } = this.getAppServices().getUiDataService();
                return presentation.masterList.findIndex(i => i.id === element.masterRef?.id);
            }
            return undefined;
        };

        try {
            const element = uiElement as IElementBaseModel;
            if (element) {
                const slideIndex = getSlideIndex(element);
                selectionService.selectedSlides.clear();
                if (slideIndex !== undefined && slideIndex >= 0) {
                    selectionService.currentSlideIndex = slideIndex;
                    selectionService.selectedElements.clear();
                    selectionService.selectedElements.add(element);
                    return { res: 'success' };
                }
            }
            return { res: 'fail', message: 'element not existed' };
        } catch (e) {
            return { res: 'fail', message: e.toString() };
        }
    }

    /**
     * 切换页面，但是不定位元素
     * @param pageIndex 页序号
     */
    public changePageIndex(pageIndex: number): IResult {
        try {
            const selectionService = this.getSelectionServices();
            selectionService.selectedSlides.clear();
            selectionService.selectedElements.clear();
            selectionService.currentSlideIndex = pageIndex;
            return { res: 'success' };
        } catch (e) {
            return { res: 'fail', message: e.toString() };
        }
    }

    /**
     * 机器审核批量高亮元素
     */
    public highlightElements(options: SlideInterface.MachineAuditHighlightProps): void {
        (this.getAppServices().editableServiceLayer as IPcEditorEditableServiceLayer).slideMachineAuditor
            ?.promise()
            .then(checker => checker.showBorder(options));
    }

    /**
     * 获取主题色Manager
     * @returns IThemeColorManager
     */
    private getThemeColorManager(): IThemeColorManager | undefined {
        return this.getAppServices().editableServiceLayer?.themeColorManager;
    }

    /**
     * 获取快捷工具Manager
     * @returns IQuickToolDialogManager
     */
    private getQuickToolDialogManager(): IDynamicService<IQuickToolDialogManager> | undefined {
        return (this.getAppServices().editableServiceLayer as IPcEditorEditableServiceLayer)?.quickToolDialogManager;
    }

    /**
     *  获取MenuManager
     * @returns IMenuManager
     */
    private getMenuManager(): IMenuManager | undefined {
        return this.getAppServices().editableServiceLayer?.menuManager;
    }

    private getSelectionServices(): ISelectionService {
        return this.getAppServices().viewableServiceLayer.selectionService;
    }

    private getMasterLayoutEditService(): IMasterLayoutEditService {
        return (this.getAppServices().editableServiceLayer as IPcEditorEditableServiceLayer)?.masterLayoutEditService;
    }

    private getAppServices(): IEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as IEditorAppServices;
    }
}
